import { useQuery as useAbandonedBrandCartNudgeQuery } from "@faire/web-api/api/v2/carts/cartToken/abandoned-brand-cart-nudge/get-hooks";
import brandCartNudgeShown from "@faire/web-api/api/v2/carts/cartToken/brand-cart-nudge-shown/post";
import { trackCartAbandonedBrandCartNudgeBrandInfoClickedClick } from "@faire/web-api/events/cart/click/abandonedBrandCartNudgeBrandInfoClicked";
import { trackCartAbandonedBrandCartNudgeViewBagClickedClick } from "@faire/web-api/events/cart/click/abandonedBrandCartNudgeViewBagClicked";
import { trackCartAbandonedBrandCartNudgeDismissedDismiss } from "@faire/web-api/events/cart/dismiss/abandonedBrandCartNudgeDismissed";
import { trackCartAbandonedBrandCartNudgeViewedView } from "@faire/web-api/events/cart/view/abandonedBrandCartNudgeViewed";
import { IAbandonedBrandCartNudgeResponse } from "@faire/web-api/faire/carts/IAbandonedBrandCartNudgeResponse";
import { route as boardsRoute } from "@faire/web-api/routes/boards";
import { route as brandPageRoute } from "@faire/web-api/routes/brand/brandToken";
import cartPage from "@faire/web-api/routes/cart";
import { route as categoryRoute } from "@faire/web-api/routes/category/c1CategoryUrlId";
import { route as saleRoute } from "@faire/web-api/routes/flash-sale";
import { route as myBrandsRoute } from "@faire/web-api/routes/my-brands";
import { route as productRoute } from "@faire/web-api/routes/product/productToken";
import { route as homeRoute } from "@faire/web-api/routes/root";
import { route as searchRoute } from "@faire/web-api/routes/search";
import { getCurrentPath } from "@faire/web/common/Path";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { matchPath, useHistory } from "react-router-dom";

import { useShouldShowSaleAnnouncementTooltip } from "@faire/retailer/components/Header/desktop/SaleAnnouncementTooltip/useShouldShowSaleAnnouncementTooltip";
import { useAppNudgeStore } from "@faire/retailer/features/Nudges/useAppNudgeStore";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { getCartToken } from "@faire/retailer/serialized-data/getCartToken";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

export interface IAbandonedBrandCartNudge {
  brandCartNudge?: IAbandonedBrandCartNudgeResponse;
  hasNudge: boolean;
  isUnderMinimumBrand: boolean;
  hasPromotion: boolean;
  isNudgeShown: boolean;
  cartLink: string;
  setIsNudgeShown: (isNudgeShown: boolean) => void;
  onNudgeClose: () => void;
  onBrandInfoClick: () => void;
  onViewBagButtonClick: () => void;
  acknowledgeNudgeShown: () => void;
}

export const useAbandonedBrandCartNudge = (): IAbandonedBrandCartNudge => {
  const { push } = useHistory();
  const { userCanPlaceOrder } = useUserStore(["userCanPlaceOrder"]);

  const { data: brandCartNudge } = useAbandonedBrandCartNudgeQuery(
    getCartToken(),
    {
      enabled: isLoggedInRetailer() && userCanPlaceOrder,
    }
  );
  const shouldShowSaleTooltip = useShouldShowSaleAnnouncementTooltip();

  const hasNudge =
    isNotUndefined(brandCartNudge?.brand_token) && !shouldShowSaleTooltip;

  const [
    {
      isAbandonedCartNudgeShown: isNudgeShown,
      hasAcknowledgedAbandonedCartNudge,
    },
    setAbandonedBrandCartNudgeStore,
  ] = useAppNudgeStore([
    "isAbandonedCartNudgeShown",
    "hasAcknowledgedAbandonedCartNudge",
  ]);
  const setIsNudgeShown = (isNudgeShown: boolean) => {
    setAbandonedBrandCartNudgeStore({
      isAbandonedCartNudgeShown: isNudgeShown,
    });
  };

  const isUnderMinimumBrand =
    !!brandCartNudge?.amount_below_minimum?.amount_cents &&
    brandCartNudge?.amount_below_minimum?.amount_cents > 0;

  const hasPromotion =
    brandCartNudge?.promo_state ===
      IAbandonedBrandCartNudgeResponse.BrandCartPromoState.SINGLE_PROMO ||
    brandCartNudge?.promo_state ===
      IAbandonedBrandCartNudgeResponse.BrandCartPromoState.MULTIPLE_PROMOS;

  const cartLink = cartPage({ nudgedBrand: brandCartNudge?.brand_token });

  const trackEventInfo = {
    nudgeType: hasPromotion ? "promo" : "standard",
    pageName: getTrackEventPageName(),
  };

  const acknowledgeNudgeShown = () => {
    if (!hasAcknowledgedAbandonedCartNudge) {
      brandCartNudgeShown(getCartToken(), {
        brand_token: brandCartNudge?.brand_token,
      });
      setAbandonedBrandCartNudgeStore({
        hasAcknowledgedAbandonedCartNudge: true,
      });
      trackCartAbandonedBrandCartNudgeViewedView(
        trackEventInfo.nudgeType,
        trackEventInfo.pageName
      );
    }
  };

  const onBrandInfoClick = () => {
    trackCartAbandonedBrandCartNudgeBrandInfoClickedClick(
      trackEventInfo.nudgeType,
      trackEventInfo.pageName
    );
  };

  const onViewBagButtonClick = () => {
    push(cartLink);
    trackCartAbandonedBrandCartNudgeViewBagClickedClick(
      trackEventInfo.nudgeType,
      trackEventInfo.pageName
    );
  };

  const onNudgeClose = () => {
    setIsNudgeShown(false);
    trackCartAbandonedBrandCartNudgeDismissedDismiss(
      hasPromotion ? "promo" : "standard",
      trackEventInfo.pageName
    );
  };

  return {
    brandCartNudge,
    hasNudge,
    isUnderMinimumBrand,
    hasPromotion,
    isNudgeShown,
    cartLink,
    setIsNudgeShown,
    onNudgeClose,
    onBrandInfoClick,
    onViewBagButtonClick,
    acknowledgeNudgeShown,
  };
};

const ELIGIBLE_PAGES = [
  homeRoute,
  searchRoute,
  categoryRoute,
  brandPageRoute,
  productRoute,
  boardsRoute,
  myBrandsRoute,
  saleRoute,
];

export const shouldAbandonedBrandCartNudge = (pathName?: string) => {
  const currentPathName = pathName ?? getCurrentPath().pathname;

  return !!ELIGIBLE_PAGES.find((whitelistPage) =>
    matchPath(currentPathName, {
      path: whitelistPage,
      exact: true,
    })
  );
};

const getTrackEventPageName = () => {
  const currentPathName = getCurrentPath().pathname;
  const currentRoute = ELIGIBLE_PAGES.find((page) =>
    matchPath(currentPathName, {
      path: page,
      exact: true,
    })
  );
  switch (currentRoute) {
    case homeRoute:
      return "homepage";
    case searchRoute:
      return "search";
    case categoryRoute:
      return "catnav";
    case brandPageRoute:
      return "brandpage";
    case productRoute:
      return "pdp";
    case boardsRoute:
      return "boards";
    case myBrandsRoute:
      return "mybrands";
    case saleRoute:
      return "flash_sale";
    default:
      return "";
  }
};
